/**
 * Meal Selection V2 - Modern Cart System
 * Matches Hydrogen Portal UX with efficient cart operations
 * v2.1 - Added tooltip hover effects
 */

(function() {
  'use strict';

  // Only run on collection pages
  if (!window.location.pathname.includes('/collections/')) return;

  // ============================================
  // STATE MANAGEMENT
  // ============================================

  const state = {
    cartItems: {},      // { variantId: quantity }
    productImages: {},  // { variantId: imageUrl } - for toolbar thumbnails
    productTitles: {},  // { variantId: title } - for tooltip on hover
    mealLimit: 8,       // Default, updated from localStorage
    isProcessing: false,
    queue: []           // Cart operation queue
  };

  // ============================================
  // DOM CACHE
  // ============================================

  const dom = {
    toolbar: null,
    thumbnails: null,   // Toolbar thumbnails container
    selectedCount: null,
    mealLimitDisplay: null,
    continueBtn: null,
    planButtons: null,  // Plan selector buttons
    cards: new Map()    // variantId -> card element
  };

  // ============================================
  // INITIALIZATION
  // ============================================

  function init() {
    // Get meal limit from localStorage (set by quiz)
    const storedMealPlan = localStorage.getItem('mealPlan');
    if (storedMealPlan) {
      try {
        const parsed = JSON.parse(storedMealPlan);
        state.mealLimit = parseInt(parsed.meals || parsed) || 8;
      } catch {
        state.mealLimit = parseInt(storedMealPlan) || 8;
      }
    }

    // Cache DOM elements
    dom.toolbar = document.getElementById('meal-toolbar-v2');
    dom.thumbnails = document.getElementById('toolbar-thumbnails');
    dom.selectedCount = document.getElementById('toolbar-selected-count');
    dom.mealLimitDisplay = document.getElementById('toolbar-meal-limit');
    dom.continueBtn = document.getElementById('toolbar-continue-btn');

    // Cache all meal cards, their images, and titles
    document.querySelectorAll('.meal-card-v2').forEach(card => {
      const variantId = card.dataset.variantId;
      if (variantId) {
        dom.cards.set(variantId, card);
        // Cache product image URL for thumbnails
        const img = card.querySelector('.meal-card-v2__image');
        if (img) {
          state.productImages[variantId] = img.src;
        }
        // Cache product title for tooltip
        const titleEl = card.querySelector('.meal-card-v2__title a');
        if (titleEl) {
          state.productTitles[variantId] = titleEl.textContent.trim();
        }
      }
    });

    // Show toolbar and set limit
    if (dom.toolbar) {
      dom.toolbar.style.display = 'block';
      if (dom.mealLimitDisplay) {
        dom.mealLimitDisplay.textContent = state.mealLimit;
      }
    }

    // Cache plan selector buttons
    dom.planButtons = document.querySelectorAll('.meal-plan-btn');

    // Attach event listeners
    attachEventListeners();

    // Initialize plan selector
    initPlanSelector();

    // Sync with current cart
    syncWithCart();

    console.log('[MealSelection V2] Initialized with limit:', state.mealLimit);
  }

  // ============================================
  // EVENT LISTENERS
  // ============================================

  function attachEventListeners() {
    // Event delegation for all card actions
    document.addEventListener('click', handleCardClick);

    // Continue button
    if (dom.continueBtn) {
      dom.continueBtn.addEventListener('click', handleContinue);
    }

    // Quick view modal for image and title clicks
    initQuickViewModal();
  }

  function handleCardClick(e) {
    const button = e.target.closest('[data-action]');
    if (!button) return;

    const action = button.dataset.action;
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    e.preventDefault();

    switch (action) {
      case 'add':
        handleAdd(variantId, button);
        break;
      case 'increase':
        handleIncrease(variantId, button);
        break;
      case 'decrease':
        handleDecrease(variantId, button);
        break;
      case 'remove':
        handleRemove(variantId, button);
        break;
    }
  }

  // ============================================
  // CART OPERATIONS
  // ============================================

  function handleAdd(variantId, button) {
    // Check limit
    const currentTotal = getTotalMealCount();
    if (currentTotal >= state.mealLimit) {
      showLimitReached(button);
      return;
    }

    // Add loading state
    button.classList.add('is-loading');
    button.disabled = true;

    // Add to cart
    addToCart(variantId, 1)
      .then(() => {
        state.cartItems[variantId] = (state.cartItems[variantId] || 0) + 1;
        updateCardUI(variantId);
        updateToolbarUI();
        trackKlaviyo(variantId, 'add');
      })
      .catch(err => {
        console.error('[MealSelection V2] Add failed:', err);
      })
      .finally(() => {
        button.classList.remove('is-loading');
        button.disabled = false;
      });
  }

  function handleIncrease(variantId, button) {
    // Check limit
    const currentTotal = getTotalMealCount();
    if (currentTotal >= state.mealLimit) {
      showLimitReached(button);
      return;
    }

    button.classList.add('is-loading');

    addToCart(variantId, 1)
      .then(() => {
        state.cartItems[variantId] = (state.cartItems[variantId] || 0) + 1;
        updateCardUI(variantId);
        updateToolbarUI();
        trackKlaviyo(variantId, 'add');
      })
      .catch(err => {
        console.error('[MealSelection V2] Increase failed:', err);
      })
      .finally(() => {
        button.classList.remove('is-loading');
      });
  }

  function handleDecrease(variantId, button) {
    const currentQty = state.cartItems[variantId] || 0;
    if (currentQty <= 1) {
      // If at 1, remove completely
      handleRemove(variantId, button);
      return;
    }

    button.classList.add('is-loading');

    updateCartItem(variantId, currentQty - 1)
      .then(() => {
        state.cartItems[variantId] = currentQty - 1;
        updateCardUI(variantId);
        updateToolbarUI();
      })
      .catch(err => {
        console.error('[MealSelection V2] Decrease failed:', err);
      })
      .finally(() => {
        button.classList.remove('is-loading');
      });
  }

  function handleRemove(variantId, button) {
    button.classList.add('is-loading');

    updateCartItem(variantId, 0)
      .then(() => {
        delete state.cartItems[variantId];
        updateCardUI(variantId);
        updateToolbarUI();
      })
      .catch(err => {
        console.error('[MealSelection V2] Remove failed:', err);
      })
      .finally(() => {
        button.classList.remove('is-loading');
      });
  }

  function handleContinue() {
    const currentTotal = getTotalMealCount();
    if (currentTotal < state.mealLimit) return;

    dom.continueBtn.classList.add('is-loading');
    dom.continueBtn.textContent = 'Loading...';

    // Redirect to cart (extras page disabled for now)
    window.location.href = '/cart';
  }

  // ============================================
  // CART API
  // ============================================

  function addToCart(variantId, quantity) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: parseInt(variantId), quantity: quantity }]
      })
    }).then(res => {
      if (!res.ok) throw new Error('Cart add failed');
      return res.json();
    });
  }

  function updateCartItem(variantId, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: variantId.toString(),
        quantity: quantity
      })
    }).then(res => {
      if (!res.ok) throw new Error('Cart update failed');
      return res.json();
    });
  }

  function syncWithCart() {
    fetch('/cart.js')
      .then(res => res.json())
      .then(cart => {
        // Reset state
        state.cartItems = {};

        // Populate from cart
        cart.items.forEach(item => {
          // Skip hidden products (like delivery method products)
          const hiddenIds = ['40634459586612', '42152016642100', '6933132083252'];
          if (hiddenIds.includes(item.variant_id.toString()) ||
              hiddenIds.includes(item.product_id.toString())) {
            return;
          }
          state.cartItems[item.variant_id] = item.quantity;
          // Also store image URL from cart if we don't have it
          if (item.image && !state.productImages[item.variant_id]) {
            state.productImages[item.variant_id] = item.image;
          }
        });

        // Update all card UIs
        dom.cards.forEach((card, variantId) => {
          updateCardUI(variantId);
        });

        // Update toolbar
        updateToolbarUI();

        console.log('[MealSelection V2] Synced with cart:', state.cartItems);
      })
      .catch(err => {
        console.error('[MealSelection V2] Cart sync failed:', err);
      });
  }

  // ============================================
  // UI UPDATES
  // ============================================

  function updateCardUI(variantId) {
    const card = dom.cards.get(variantId);
    if (!card) return;

    const quantity = state.cartItems[variantId] || 0;
    const addBtn = card.querySelector('.meal-card-v2__add-btn');
    const qtySelector = card.querySelector('.meal-card-v2__quantity-selector');
    const qtyCount = card.querySelector('.meal-card-v2__qty-count');

    if (quantity > 0) {
      // Show quantity selector, hide add button
      if (addBtn) addBtn.style.display = 'none';
      if (qtySelector) qtySelector.style.display = 'flex';
      if (qtyCount) qtyCount.textContent = quantity;
      card.classList.add('is-selected');
    } else {
      // Show add button, hide quantity selector
      if (addBtn) addBtn.style.display = 'block';
      if (qtySelector) qtySelector.style.display = 'none';
      card.classList.remove('is-selected');
    }

    // Disable/enable add buttons based on limit
    updateAddButtonStates();
  }

  function updateToolbarUI() {
    const total = getTotalMealCount();

    if (dom.selectedCount) {
      dom.selectedCount.textContent = total;
    }

    if (dom.continueBtn) {
      // Enable only when exactly at limit (not over, not under)
      const isComplete = total === state.mealLimit;
      dom.continueBtn.disabled = !isComplete;
    }

    // Update thumbnails
    renderThumbnails();

    // Update add button states
    updateAddButtonStates();

    // Update plan selector button states
    updatePlanButtonStates();

    // Show/hide over-limit error message
    updateOverLimitMessage();
  }

  function renderThumbnails() {
    if (!dom.thumbnails) return;

    // Get current thumbnails in toolbar
    const currentThumbs = dom.thumbnails.querySelectorAll('.meal-toolbar-v2__thumbnail');
    const currentVariantIds = new Set();
    currentThumbs.forEach(thumb => {
      currentVariantIds.add(thumb.dataset.variantId);
    });

    // Get variant IDs that should be in toolbar (one per quantity)
    const neededThumbs = [];
    Object.entries(state.cartItems).forEach(([variantId, qty]) => {
      for (let i = 0; i < qty; i++) {
        neededThumbs.push(variantId);
      }
    });

    // Clear and rebuild thumbnails
    // First, animate out removed items
    const variantIdsNeeded = new Set(Object.keys(state.cartItems));
    currentThumbs.forEach(thumb => {
      if (!variantIdsNeeded.has(thumb.dataset.variantId)) {
        thumb.classList.add('is-removing');
        setTimeout(() => thumb.remove(), 150);
      }
    });

    // Then rebuild after animation
    setTimeout(() => {
      // Clear container
      dom.thumbnails.innerHTML = '';

      if (neededThumbs.length === 0) {
        // Show empty hint
        dom.thumbnails.innerHTML = '<span class="meal-toolbar-v2__empty-hint">Your meals will appear here</span>';
        return;
      }

      // Add thumbnails for each meal (one per quantity)
      neededThumbs.forEach((variantId, index) => {
        const imageUrl = state.productImages[variantId];
        if (!imageUrl) return;

        const title = state.productTitles[variantId] || '';

        // Wrap in span for tooltip
        const wrapper = document.createElement('span');
        wrapper.className = 'meal-toolbar-v2__thumb-wrap';
        wrapper.dataset.variantId = variantId;
        wrapper.style.animationDelay = `${index * 0.03}s`;

        const thumb = document.createElement('img');
        thumb.className = 'meal-toolbar-v2__thumbnail';
        thumb.src = imageUrl;
        thumb.alt = title;

        // Create tooltip element
        if (title) {
          const tooltip = document.createElement('span');
          tooltip.className = 'meal-toolbar-v2__tooltip';
          tooltip.textContent = title;
          wrapper.appendChild(tooltip);
        }

        wrapper.appendChild(thumb);
        dom.thumbnails.appendChild(wrapper);
      });
    }, currentThumbs.length > 0 ? 150 : 0);
  }

  function updateAddButtonStates() {
    const total = getTotalMealCount();
    const atLimit = total >= state.mealLimit;

    document.querySelectorAll('.meal-card-v2__add-btn').forEach(btn => {
      btn.disabled = atLimit;
    });

    document.querySelectorAll('.meal-card-v2__qty-btn--plus').forEach(btn => {
      btn.disabled = atLimit;
    });
  }

  function showLimitReached(button) {
    // Shake animation
    button.style.animation = 'shake 0.3s ease-in-out';
    setTimeout(() => {
      button.style.animation = '';
    }, 300);
  }

  // ============================================
  // QUICK VIEW MODAL
  // ============================================

  function initQuickViewModal() {
    const modal = document.getElementById('quick-view-modal');
    const content = document.getElementById('quick-view-content');

    if (!modal || !content) {
      console.log('[MealSelection V2] Quick view modal elements not found');
      return;
    }

    // Handle clicks on product images and titles
    document.body.addEventListener('click', function(e) {
      // Check if clicked on image link or title link
      const link = e.target.closest('.product-card__figure a') || e.target.closest('.product-card__title a');
      if (!link) return;

      e.preventDefault();
      const productUrl = link.getAttribute('href');
      const quickViewUrl = `${productUrl}?view=quick-view`;

      // Show loading state
      content.innerHTML = '<div style="text-align: center; padding: 40px;"><p>Loading...</p></div>';
      modal.style.display = 'flex';

      fetch(quickViewUrl)
        .then(response => response.text())
        .then(html => {
          content.innerHTML = html;
          content.scrollTop = 0;
          modal.scrollTop = 0;

          // Attach close button logic
          const injectedCloseBtn = content.querySelector('.quick-view-close');
          if (injectedCloseBtn) {
            injectedCloseBtn.addEventListener('click', () => closeQuickViewModal(modal, content));
          }
        })
        .catch(err => {
          console.error('[MealSelection V2] Quick View load failed', err);
          content.innerHTML = '<div style="text-align: center; padding: 40px;"><p>Failed to load product details.</p></div>';
        });
    });

    // Close on overlay click
    modal.addEventListener('click', (e) => {
      if (e.target.classList.contains('quick-view-overlay')) {
        closeQuickViewModal(modal, content);
      }
    });

    // Close button outside content
    const closeBtn = document.getElementById('quick-view-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => closeQuickViewModal(modal, content));
    }

    console.log('[MealSelection V2] Quick view modal initialized');
  }

  function closeQuickViewModal(modal, content) {
    modal.style.display = 'none';
    content.innerHTML = '';
  }

  // ============================================
  // PLAN SELECTOR
  // ============================================

  function initPlanSelector() {
    if (!dom.planButtons || dom.planButtons.length === 0) {
      console.log('[MealSelection V2] No plan selector buttons found');
      return;
    }

    // Highlight current plan
    updatePlanButtonStates();

    // Attach click handlers
    dom.planButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        const newPlan = parseInt(btn.dataset.plan);
        handlePlanChange(newPlan);
      });
    });

    console.log('[MealSelection V2] Plan selector initialized');
  }

  function handlePlanChange(newPlan) {
    // Update localStorage
    localStorage.setItem('mealPlan', newPlan.toString());

    // Also update window.name fallback (Safari / Private Mode)
    try {
      const fallback = JSON.parse(window.name || "{}");
      fallback.mealPlan = newPlan.toString();
      window.name = JSON.stringify(fallback);
    } catch (err) {
      console.warn("[MealSelection V2] Could not store mealPlan in window.name:", err);
    }

    // Update customer metafield via Vercel backend (non-blocking)
    const email = localStorage.getItem('email');
    if (email) {
      fetch("https://homecooks-backend.vercel.app/api/create-customer", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email,
          goals: [],
          diet: "",
          plan: newPlan.toString()
        })
      }).then(() => {
        console.log('[MealSelection V2] Customer metafield updated for plan:', newPlan);
      }).catch(err => {
        console.warn('[MealSelection V2] Failed to update customer metafield:', err);
      });
    }

    // Update state
    state.mealLimit = newPlan;

    // Update toolbar display
    if (dom.mealLimitDisplay) {
      dom.mealLimitDisplay.textContent = newPlan;
    }

    // Re-highlight correct button
    updatePlanButtonStates();

    // Update toolbar UI (including error message)
    updateToolbarUI();

    console.log('[MealSelection V2] Plan changed to:', newPlan);
  }

  function updatePlanButtonStates() {
    if (!dom.planButtons) return;

    const priceEl = document.getElementById('plan-price');

    dom.planButtons.forEach(btn => {
      const plan = parseInt(btn.dataset.plan);
      const isCurrent = plan === state.mealLimit;

      btn.classList.toggle('active', isCurrent);
      btn.title = `${plan} meals per week`;

      // Update price display for active plan
      if (isCurrent && priceEl && btn.dataset.price) {
        priceEl.textContent = `£${btn.dataset.price}/meal`;
      }
    });
  }

  function updateOverLimitMessage() {
    const currentCount = getTotalMealCount();
    const overBy = currentCount - state.mealLimit;

    // Find or create error message element
    let errorEl = document.getElementById('plan-over-limit-msg');

    if (overBy > 0) {
      // Need to show error
      if (!errorEl) {
        errorEl = document.createElement('span');
        errorEl.id = 'plan-over-limit-msg';
        errorEl.className = 'meal-toolbar-v2__error';
        // Insert after the counts
        const countsEl = document.querySelector('.meal-toolbar-v2__counts');
        if (countsEl) {
          countsEl.appendChild(errorEl);
        }
      }
      errorEl.textContent = `Please remove ${overBy} meal${overBy > 1 ? 's' : ''} for this plan`;
      errorEl.style.display = 'inline';
    } else {
      // Hide error
      if (errorEl) {
        errorEl.style.display = 'none';
      }
    }
  }

  // ============================================
  // UTILITIES
  // ============================================

  function getTotalMealCount() {
    return Object.values(state.cartItems).reduce((sum, qty) => sum + qty, 0);
  }

  function trackKlaviyo(variantId, action) {
    if (!window.klaviyo) return;

    setTimeout(() => {
      try {
        const card = dom.cards.get(variantId);
        const productTitle = card?.querySelector('.meal-card-v2__title a')?.textContent?.trim() || 'Unknown';

        window.klaviyo.push(['track', 'Added to Cart', {
          'AddedItemProductName': productTitle,
          'AddedItemProductID': variantId,
          'AddedItemQuantity': 1
        }]);

        console.log('[MealSelection V2] Klaviyo tracked:', productTitle);
      } catch (err) {
        console.warn('[MealSelection V2] Klaviyo tracking failed:', err);
      }
    }, 0);
  }

  // ============================================
  // CSS ANIMATIONS
  // ============================================

  const style = document.createElement('style');
  style.textContent = `
    @keyframes shake {
      0%, 100% { transform: translateX(0); }
      25% { transform: translateX(-3px); }
      75% { transform: translateX(3px); }
    }
  `;
  document.head.appendChild(style);

  // ============================================
  // STARTUP
  // ============================================

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
